import {
  compareHuge,
  huge,
  hugeZero,
  minHuge,
  type HugeNumber,
} from "@/shared/numbers";

export function computePassiveResourceGain(input: {
  readonly gainPerSecond: HugeNumber;
  readonly allocation: number;
  readonly hiddenGrowthPerSecond: HugeNumber;
  readonly dtSeconds: number;
  readonly passiveResourceBalanceBase: number;
}): HugeNumber {
  if (input.allocation <= 0) return hugeZero();
  const allocationScale = input.allocation / input.passiveResourceBalanceBase;
  return input.gainPerSecond
    .mul(input.dtSeconds)
    .add(
      input.hiddenGrowthPerSecond.mul(0.5 * input.dtSeconds * input.dtSeconds),
    )
    .mul(allocationScale);
}

export function computeTimToHovRefinement(input: {
  readonly availableTim: HugeNumber;
  readonly cellGainPerSecond: HugeNumber;
  readonly dtSeconds: number;
  readonly timHovActivationEfficiency: number;
}): {
  readonly consumedTim: HugeNumber;
  readonly refinedHovGain: HugeNumber;
} {
  const maxConsume = huge(1 * input.dtSeconds);
  const consumedTim = minHuge(input.availableTim, maxConsume);
  const boostRatio =
    compareHuge(maxConsume, 0) > 0 ? consumedTim.div(maxConsume).toNumber() : 0;

  return {
    consumedTim,
    refinedHovGain: input.cellGainPerSecond
      .mul(boostRatio)
      .mul(input.dtSeconds)
      .mul(input.timHovActivationEfficiency),
  };
}

// TODO: computeActiveTimHoverGain から HoverGain を用いない名称にする
export function computeActiveTimHoverGain(input: {
  readonly availableHov: HugeNumber;
  readonly hovGainAtStart: HugeNumber;
  readonly cellTimGainPerSecond: HugeNumber;
  readonly dtSeconds: number;
}): {
  readonly consumedHov: HugeNumber;
  readonly timGain: HugeNumber;
} {
  const maxConsumeHov = input.hovGainAtStart.mul(input.dtSeconds);
  const consumedHov = minHuge(input.availableHov, maxConsumeHov);
  const boostRatio =
    compareHuge(maxConsumeHov, 0) > 0
      ? consumedHov.div(maxConsumeHov).toNumber()
      : 0;
  const boostedTimGainPerSecond = input.cellTimGainPerSecond.add(
    input.hovGainAtStart.mul(boostRatio),
  );

  return {
    consumedHov,
    timGain: boostedTimGainPerSecond.mul(input.dtSeconds),
  };
}

export function computeHiddenGainGrowthPerSecond(input: {
  readonly allocation: number;
  readonly growthPerAllocationPerSecond: number;
}): HugeNumber {
  return huge(input.allocation * input.growthPerAllocationPerSecond);
}
